import {
  P2Alert,
  P2Button,
  P2ButtonGroup,
  P2Modal,
  P2TextDescription,
} from "react-bootstrap-common-script";
import { SectionCard } from "../../../components/card";
import { Stepper } from "../../../components/stepper";
import { R2StageEStepperItems } from "../../../utils/shared-dropdown";
import { LottieLoader } from "../../../components/loader";
import { Separator } from "../../../components/separator";
import { TextSubheader } from "../../../components/text/text-subheader";
import ApplicationCooLayout from "../../../layout/application-coo-layout";
import { Helpers } from "../../../utils/helper";
import { shared_documents_list } from "../../../utils/shared/documents-list";
import Documents from "../../application-coo-stage-d/sections/documents";
import Message from "../../application-coo-stage-d/sections/message";
import ApplicationUpdateEndorseForm from "../../application-coo-stage-d/step-1/application-update-endorse-form";
import ReviewingPage from "../../application-coo-stage-d/step-1/reviewing-page";
import { IErrors, useForm } from "../../../hooks/use-form";
import {
  StageDDocumentsInfo,
  StageDEndorseFormInfo,
  StageDReviewApplication,
} from "../../../models/classes/stage-d/Review-Application.class";
import { useEffect, useMemo, useState } from "react";
import { FileItem } from "../../../models/polymer/Document.model";
import { cloneDeep, uniq } from "lodash";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../redux/hook";
import { PolymerAxios } from "../../../utils/axios";
import { v4 as uuidv4 } from "uuid";
import { API_ApplicationReply } from "../../../utils/api";
import CPFWithdrawalEService from "../segments/cpf-withdrawal-e-service";
import moment from "moment";
import { StepperInterface } from "../../../models/interfaces/stepper-item.interface";

interface FieldValidationInterface {
  isValid: boolean;
  isPartial: boolean;
}

interface StageEEViewProps {
  data: StageDReviewApplication | undefined;
  refetch: () => void;
  dataIsUninitialized: boolean;
  pendingItems: string[];
  setPendingItems: (value: string[]) => void;
  isCpfServiceRequired: boolean;
  isPostCompletionReviewing: boolean;
  setIsPostCompletionReviewing: (value: boolean) => void;
  currentOwnerIncludedEndorseManuallySent: StageDEndorseFormInfo[];
}

const R2StageEStepperItemsE3: StepperInterface[] = [
  ...R2StageEStepperItems,
  {
    id: 3,
    title: "Post Completion",
    link: "/application-coo/stage-e/prepare-for-completion",
  },
];

const StageE3View = ({
  data,
  refetch,
  dataIsUninitialized,
  pendingItems,
  setPendingItems,
  isCpfServiceRequired,
  isPostCompletionReviewing,
  setIsPostCompletionReviewing,
  currentOwnerIncludedEndorseManuallySent,
}: StageEEViewProps) => {
  const navigate = useNavigate();
  const application_uuid = useAppSelector(
    (state) => state.app.ApplicationIdSlice.applicationId
  );
  const loginUser = useAppSelector(
    (state) => state.auth.AuthenticationSlice.loginUser
  );
  const singpassToken = useAppSelector(
    (state) => state.auth.AuthenticationSlice.singpassToken
  );

  const startApplicationFormData = useAppSelector(
    (state) => state.app.ApplicationStartSlice.formData
  );
  const [filesItemsStorage, setFileItemsStorage] = useState<FileItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setisLoading] = useState(false);
  // Initialize the form state using the custom useForm hook.
  const {
    values: applicationReviewValues,
    setFormValue: applicationReviewSetFormValue,
  }: {
    handleChange: any;
    values: StageDReviewApplication;
    errors: IErrors;
    formValid: boolean;
    setFormValue: (update: any) => void;
  } = useForm(
    new StageDReviewApplication(), // initial form model
    () => {}, // onChange callback (if needed)
    () => {} // onSubmit callback (if needed)
  );

  const messageType = data?.message_type ?? "GENERIC";
  const normalFolders =
    messageType === "GENERIC"
      ? applicationReviewValues.folders
      : applicationReviewValues.folders.filter(
          (item) => item.name !== "Letter of Lawyer's Representation"
        );

  /** -------------------------------
   * Custom Validations
   --------------------------------- */
  const isDocumentsValid = (): FieldValidationInterface => {
    // Extract the labels of required documents from 'normalFolders'
    // Only include folders where 'is_request_document' is true.
    const requiredDocumentLabels = normalFolders
      .filter((folder) => folder.is_request_document)
      .map((folder) => folder.name);

    // Extract the labels of uploaded documents from 'filesItemsStorage'
    // Only include files that have a non-empty file_id.
    const uploadedDocumentLabels = filesItemsStorage
      .filter((file) => file.file_id !== "")
      .map((file) => file.label);

    // Determine if all required documents have been uploaded.
    // "isValid" is true when:
    // - There is at least one required document, and
    // - Every required document label is found in the uploaded documents.
    const isValid =
      requiredDocumentLabels.length > 0 &&
      requiredDocumentLabels.every((label) =>
        uploadedDocumentLabels.includes(label)
      );

    // Determine if the upload is partial.
    // "isPartiallyValid" is true if:
    // - Some required documents have been uploaded, and
    // - Some required documents are still missing.
    const isPartiallyValid =
      requiredDocumentLabels.some((label) =>
        uploadedDocumentLabels.includes(label)
      ) &&
      requiredDocumentLabels.some(
        (label) => !uploadedDocumentLabels.includes(label)
      );

    // The "partial" flag should be true only if the documents are not fully valid
    // and there is partial progress.
    // If all required documents are valid, partial must be false.
    const isPartial = isValid ? false : isPartiallyValid;

    return { isValid, isPartial };
  };

  const isMessagesValid = (): FieldValidationInterface => {
    // Extract the message information from the form values for clarity.
    const messages = applicationReviewValues.message_info;

    // Check if all messages are fully valid.
    // A message is considered fully valid if it is saved (is_saved is truthy)
    // and it has a non-empty reply.
    const valid =
      messages.length > 0 &&
      messages.every(
        (msg) => msg.is_saved && msg.reply && msg.reply.length > 0
      );

    // Get the unique saved state values from all messages.
    // This returns an array such as [true] if all messages are saved,
    // or [true, false] if there's a mix.
    const messageSaveStates = uniq(
      messages.map((msg) => msg.is_saved ?? false)
    );

    // Determine if the messages are partially filled.
    // "Partial" means that:
    // - There is at least one message, and either:
    //    a) Not all messages share the same saved status (i.e., a mix exists), or
    //    b) Some messages have a reply (non-empty) and at least one message is unsaved.
    const isPartial =
      (messages.length > 0 && messageSaveStates.length !== 1) ||
      (messages.some((msg) => msg.reply && msg.reply.length > 0) &&
        messageSaveStates.includes(false));

    // If the messages are fully valid, then they cannot be considered partially filled.
    const partial = !valid && isPartial;

    return { isValid: valid, isPartial: partial };
  };

  // Verify partial submission conditions
  const isSubmitButtonEnabled = (): boolean => {
    const docValidation = isDocumentsValid();
    const msgValidation = isMessagesValid();

    const hasItemsToSubmit = submittingItemsCount > 0;
    console.log(hasItemsToSubmit);
    console.log("hasItemsToSubmit");

    return (
      !(docValidation.isPartial || msgValidation.isPartial) &&
      (docValidation.isValid || msgValidation.isValid) &&
      hasItemsToSubmit
    );
  };

  const submittingItemsCount = useMemo(() => {
    const isMessagesValidResult = isMessagesValid().isValid;
    const totalValidMessages = isMessagesValidResult
      ? applicationReviewValues.message_info.length
      : 0;

    const isDocumentsValidResult = isDocumentsValid().isValid;
    const totalValidDocuments = isDocumentsValidResult
      ? normalFolders.length
      : 0;

    const submitMessageCount = pendingItems.includes("messages")
      ? totalValidMessages
      : 0;

    const submitDocumentsCount =
      pendingItems.includes("documents") && isDocumentsValidResult
        ? totalValidDocuments
        : 0;

    return submitMessageCount + submitDocumentsCount;
  }, [
    applicationReviewValues,
    applicationReviewValues?.message_info,
    isDocumentsValid,
    isMessagesValid,
    normalFolders,
    pendingItems,
  ]);

  const handleSecondaryButton = async () => {
    // to navigate => when get destination
    await submitApplicationUpdate();
    setIsModalOpen(false);
  };

  useEffect(() => {
    applicationReviewSetFormValue(data);
  }, [data, applicationReviewSetFormValue]);

  useEffect(() => {
    if (isSubmitted === true) {
      setTimeout(() => {
        setIsSubmitted(false);
      }, 1500);
    }
  }, [isSubmitted]);

  const backNavigate = () => {
    navigate(`/application-coo?application_id=${application_uuid}`);
  };

  const processSubmitApiData = () => {
    const submittingMessages = [
      ...applicationReviewValues?.message_info
        .filter(
          (msg) => msg.type !== "SOLICITOR" && msg.reply && msg.reply.length > 0
        )
        .map((msg) => {
          return {
            due_date: msg.due_date,
            received_date: msg.received_date,
            subject: msg.subject,
            message: msg.message,
            reply: msg.reply,
            group_uuid: msg.group_uuid, //Groupd uuid
            parent_uuid: msg.uuid, //existing uuid will tie to parent_uuid
            uuid: uuidv4(), //Generate new uuid for to biome side (can be any unique value here)
            status: msg.status,
          };
        }),
    ];

    const clonedFolders = cloneDeep(applicationReviewValues.folders);

    for (const folder of clonedFolders) {
      for (const comment of folder.comments) {
        const files = filesItemsStorage.filter(
          (item) => item.label === folder.name && item.file_id !== ""
        );
        comment.documents = files.map(
          (item) =>
            new StageDDocumentsInfo({
              name: item.file_content.file_name ?? "",
              submitted_date: item.submitted_date ?? "",
              size: item?.file_content?.size ?? 0,
              uuid: item.file_id.toString(),
              key: item.key,
            })
        );
      }
    }

    const finalProcessedFolders = clonedFolders.filter((item: any) =>
      item.comments.every((item: any) => item.documents.length !== 0)
    );
    return {
      ...applicationReviewValues,
      message_info: applicationReviewValues.message_info.every(
        (item) => item?.reply && item?.reply?.length > 0
      )
        ? submittingMessages
        : [],
      folders: filesItemsStorage.length > 0 ? finalProcessedFolders : [],
    };
  };

  // SUBMISSION
  const submitApplicationUpdate = async () => {
    const submittingData = processSubmitApiData();
    const submittingItems: string[] = [];
    const messagesCount = applicationReviewValues.message_info.filter(
      (item) => item?.reply && item?.reply.length > 0
    ).length;
    const filesCount = filesItemsStorage.length;
    if (messagesCount > 0) {
      submittingItems.push("messages");
    }

    if (filesCount > 0) {
      submittingItems.push("documents");
    }

    const updatedPendingItems = [...pendingItems].filter(
      (item) => !submittingItems.includes(item)
    );

    try {
      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(
        API_ApplicationReply,
        submittingData
      );
      setPendingItems(updatedPendingItems);
      setFileItemsStorage([]);
      setIsSubmitted(true);

      if (updatedPendingItems.length === 0) {
        setIsPostCompletionReviewing(true);
      } else {
        refetch();
      }
    } catch (error) {
      console.log("Error submitting application update", error);
    }
  };

  const ModalMessage = useMemo(() => {
    let messagesCount = 0;
    if (pendingItems.includes("messages")) {
      const reviewMessageCount = applicationReviewValues.message_info.filter(
        (item) => item?.reply && item.reply.length > 0
      ).length;
    }
    const filesCount = pendingItems.includes("documents")
      ? filesItemsStorage.length
      : 0;
    return (
      <div style={{ marginBottom: 24 }}>
        <p style={{ marginBottom: "0.5rem" }}>
          HDB will receive your submission of the following:
        </p>
        <ul className="polymer-reply-confirmation-modal-listing">
          {messagesCount > 0 && (
            <li style={{ marginBottom: "1rem" }}>
              {messagesCount} replied message{messagesCount > 1 ? "s" : ""}
            </li>
          )}
          {filesCount > 0 && (
            <li>
              {filesCount} submitted document{filesCount > 1 ? "s" : ""}
            </li>
          )}
        </ul>
      </div>
    );
  }, [applicationReviewValues, filesItemsStorage, pendingItems]);

  const latestDueDate = useMemo(() => {
    const messageDueDates =
      data?.message_info
        .filter((item) => item?.due_date && item.due_date.length > 0)
        .map((item) => moment(item.due_date).format("yyyy-MM-DD")) ?? [];
    const foldersDueDates =
      data?.folders
        .filter((item) => item?.due_date && item.due_date.length > 0)
        .map((item) => moment(item.due_date).format("yyyy-MM-DD")) ?? [];
    const solicitorMessageDueDates =
      data?.solicitor_message_info
        ?.filter((item) => item?.due_date && item.due_date.length > 0)
        .map((item) => moment(item.due_date).format("yyyy-MM-DD")) ?? [];
    const endorseformDueDates =
      data?.endorse_forms === undefined
        ? []
        : data?.endorse_forms
            .filter((item) => item?.due_date && item.due_date.length > 0)
            .map((item) => moment(item.due_date).format("yyyy-MM-DD")) ?? [];

    const dueDates = messageDueDates
      .concat(foldersDueDates)
      .concat(solicitorMessageDueDates)
      .concat(endorseformDueDates)
      .sort((a, b) => b.localeCompare(a));
    return dueDates.length > 0 ? dueDates[0] : moment().format("yyyy-MM-DD");
  }, [data]);

  // NOTE: The display order is as such
  // CPF E-Service
  // EndorseForms
  // Message
  // Documents

  return (
    <ApplicationCooLayout>
      <div className="polymer-application-coo__wrapper cpf-wrapper">
        <TextSubheader
          subheaderMain="Stage E: Prepare For Completion"
          subheaderSub={startApplicationFormData.selected_hdb_property?.address}
          paddingLeftRight={42}
        />
        <Stepper activeStep={3} items={R2StageEStepperItemsE3} />

        <div>
          {!isSubmitted && pendingItems.length > 0 && (
            <div className="polymer-stage-d-alert">
              {" "}
              <P2Alert
                variant={"warning"}
                text={`${
                  pendingItems.length
                } item(s) pending your actions. Please respond by ${Helpers.formatDate(
                  latestDueDate
                )} or your application will be cancelled.`}
              />
            </div>
          )}

          {isSubmitted && (
            <div className="polymer-stage-d-alert">
              {" "}
              <P2Alert
                variant={"success"}
                text={"Your submission has been received by HDB."}
              />
            </div>
          )}

          <div className="d-flex justify-content-center">
            <SectionCard
              required={false}
              className="flex-fill"
              activeStep={3}
              stepperItems={[R2StageEStepperItemsE3[0]]}
              titleChildren={
                <P2TextDescription title={`Post Completion`} />
              }
              contentChildren={
                <>
                  {isLoading && <LottieLoader />}
                  {!isLoading && isPostCompletionReviewing && (
                    <ReviewingPage message="HDB is reviewing your application. We will let you know if there are any updates on the application." />
                  )}
                  {/* <Separator variant="solid" style={{padding:"32px 0 24px 0"}}/> */}
                  {!isLoading && !isPostCompletionReviewing && (
                    <div>
                      <div className="polymer-stage-e-reminder px-4 pb-4">
                        <P2TextDescription
                          color="#333"
                          title={"Follow-up items after Completion"}
                          description={
                            "Please complete the following additional item(s) after the completion appointment."
                          }
                        />
                      </div>
                      {/* CPF */}
                      {isCpfServiceRequired && (
                        <>
                          {" "}
                          <CPFWithdrawalEService />{" "}
                          {pendingItems.length > 0 && (
                            <Separator
                              variant="solid"
                              style={{ paddingBlock: "2rem" }}
                            />
                          )}
                        </>
                      )}

                      {/* Form */}
                      {pendingItems.includes("endorse_forms") && (
                        <>
                          <ApplicationUpdateEndorseForm
                            data={currentOwnerIncludedEndorseManuallySent}
                            loginUser={loginUser?.uinfin}
                            singpassToken={singpassToken ?? ''}
                            onRefetch={refetch}
                            isLoading={isLoading}
                            isUninitialized={dataIsUninitialized}
                            applicationUuid={application_uuid}
                          />{" "}
                          <div style={{ marginTop: "24px" }}></div>
                        </>
                      )}

                      {/* Separator logic for after endorse_forms*/}
                      {pendingItems.includes("endorse_forms") &&
                        (pendingItems.includes("messages") ||
                          pendingItems.includes("documents")) && (
                          <Separator
                            variant="solid"
                            style={{ paddingBottom: "2rem" }}
                          />
                        )}

                      {/* Message + Doc */}
                      {pendingItems.includes("messages") && (
                        <>
                          <Message
                            value={applicationReviewValues}
                            setFormValue={applicationReviewSetFormValue}
                            hasSaveButton={true}
                            hasFixedTitle={true}
                          />{" "}
                          <div style={{ marginTop: "24px" }}></div>
                        </>
                      )}

                      {/* Separator */}
                      {pendingItems.includes("messages") &&
                        pendingItems.includes("documents") && (
                          <Separator
                            variant="solid"
                            style={{ paddingBottom: "2rem" }}
                          />
                        )}

                      {pendingItems.includes("documents") &&
                        normalFolders.length > 0 && (
                          <Documents
                            folders={normalFolders}
                            applicationReviewSetFormValue={
                              applicationReviewSetFormValue
                            }
                            document_list={shared_documents_list}
                            filesItemsStorage={filesItemsStorage}
                            setFileItemsStorage={setFileItemsStorage}
                            isFirstSection={true}
                          />
                        )}
                    </div>
                  )}
                </>
              }
            />
          </div>
        </div>

        <div className="cpf-wrapper_button-wrapper">
          <Separator />
          <div>
            <div
              className="polymer-button-group-wrapper"
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "24px",
              }}
            >
              <P2ButtonGroup>
                <P2Button variant={"outline-primary"} onClick={backNavigate}>
                  Back
                </P2Button>
                {!isPostCompletionReviewing && (
                  <P2Button
                    onClick={() => setIsModalOpen(true)}
                    disabled={!isSubmitButtonEnabled()}
                    variant="primary-1"
                  >
                    Submit
                  </P2Button>
                )}
              </P2ButtonGroup>
            </div>
          </div>
        </div>
      </div>

      <P2Modal
        isOpen={isModalOpen}
        onCloseModal={() => setIsModalOpen(false)}
        variant="default"
        title={
          <p
            style={{ marginBottom: 16, fontWeight: 700, fontSize: "20px" }}
          >{`You are submitting: ${submittingItemsCount} item${
            submittingItemsCount > 1 ? "s" : ""
          }`}</p>
        } // in future to change to .length
        primaryButtonText="No, continue editing"
        secondaryButtonText="Yes, submit to HDB"
        primaryButtonFunction={() => setIsModalOpen(false)}
        secondaryButtonFunction={handleSecondaryButton}
        children={ModalMessage}
      />
    </ApplicationCooLayout>
  );
};
export default StageE3View;
